// External libraries
import { useMediaQuery, useTheme } from '@mui/material'
import { useTranslations } from 'next-intl'
import React, { useEffect, useMemo } from 'react'

// Internal absolute imports
import useClevertapForSignin from '@/src/hooks/analytics/clevertap/signin'
import { OtpStep, useOtpFlow } from '@/src/hooks/useOtpFlow'
import { maskEmail } from '@/src/utils/auth'

// Internal relative imports
import { OtpVerificationModalDesktop } from './OtpVerificationModalDesktop'
import { OtpVerificationModalMobile } from './OtpVerificationModalMobile'

interface OtpVerificationModalWrapperProps {
    open: boolean
    onClose: () => void
    onMenuClose?: () => void
}

const OtpVerificationModalWrapper: React.FC<OtpVerificationModalWrapperProps> = ({ open, onClose, onMenuClose }) => {
    // Device detection
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('md'))
    // Translations
    const t = useTranslations('otpModal')

    // CleverTap analytics
    const { triggerSigninEvent } = useClevertapForSignin()

    // Use the centralized OTP flow hook - this replaces ALL the logic that was here!
    const otpFlow = useOtpFlow({
        onClose,
        onMenuClose,
        translate: t,
        isMobile
    })

    // Track signin modal open event - user is not logged in at this point
    useEffect(() => {
        if (open) {
            triggerSigninEvent('Air_UI_Action', {
                action_name: 'signin_open',
                action_type: 'PAGE_VIEW',
                action_value: 'success'
            })
        }
    }, [open])

    // Compute hero section content based on current step and state (Business Logic Layer)
    const heroContent = useMemo(() => {
        // For email input step with Welcome Back flow
        if (otpFlow.currentStep === OtpStep.EMAIL_INPUT && otpFlow.isWelcomeBack) {
            return {
                title: t('hero.welcomeBack.title'),
                subtitle: t('hero.welcomeBack.subtitle'),
                brandName: t('hero.brandName')
            }
        }

        // Default hero for all other steps
        return {
            title: t('hero.default.title'),
            subtitle: t('hero.default.subtitle'),
            brandName: t('hero.brandName')
        }
    }, [otpFlow.currentStep, otpFlow.isWelcomeBack, t])

    // Compute masked email (Business Logic Layer)
    const maskedEmail = useMemo(
        () => (otpFlow.existingEmail ? maskEmail(otpFlow.existingEmail) : null),
        [otpFlow.existingEmail]
    )

    // Prepare data for presentation components (memoized to prevent unnecessary re-renders)
    const flowData = useMemo(
        () => ({
            currentStep: otpFlow.currentStep,
            error: otpFlow.error,
            errorState: otpFlow.errorState,
            isWelcomeBack: otpFlow.isWelcomeBack,
            existingEmail: otpFlow.existingEmail,
            maskedEmail,
            isGoogleFlow: otpFlow.isGoogleFlow,
            googleUserData: otpFlow.googleUserData,

            // Hero section content (computed in Logic Layer)
            heroTitle: heroContent.title,
            heroSubtitle: heroContent.subtitle,
            heroBrandName: heroContent.brandName,

            // Mobile data
            mobileCountryCode: otpFlow.mobileCountryCode,
            selectedCountryCode: otpFlow.selectedCountryCode,
            countryData: otpFlow.countryData,
            isCountryDataLoading: otpFlow.isCountryDataLoading,
            mobileNumber: otpFlow.mobileNumber,
            mobileOtp: otpFlow.mobileOtp,

            // Email data
            email: otpFlow.email,
            emailOtp: otpFlow.emailOtp,

            // Loading states
            isSendingOtp: otpFlow.isSendingOtp,
            isVerifyingOtp: otpFlow.isVerifyingOtp,

            // Timer states
            mobileResendTimer: otpFlow.mobileResendTimer,
            emailResendTimer: otpFlow.emailResendTimer
        }),
        [
            otpFlow.currentStep,
            otpFlow.error,
            otpFlow.errorState,
            otpFlow.isWelcomeBack,
            otpFlow.existingEmail,
            maskedEmail,
            otpFlow.isGoogleFlow,
            otpFlow.googleUserData,
            heroContent.title,
            heroContent.subtitle,
            heroContent.brandName,
            otpFlow.mobileCountryCode,
            otpFlow.selectedCountryCode,
            otpFlow.countryData,
            otpFlow.isCountryDataLoading,
            otpFlow.mobileNumber,
            otpFlow.mobileOtp,
            otpFlow.email,
            otpFlow.emailOtp,
            otpFlow.isSendingOtp,
            otpFlow.isVerifyingOtp,
            otpFlow.mobileResendTimer,
            otpFlow.emailResendTimer
        ]
    )

    // Handlers are already stable (useCallback in the hook), no need to memoize
    const flowHandlers = {
        onClose: otpFlow.handleClose,
        handleOnCross: otpFlow.handleOnCross,

        // Mobile handlers
        handleCountryCodeChange: otpFlow.handleCountryCodeChange,
        handleMobileNumberChange: otpFlow.handleMobileNumberChange,
        handleMobileOtpChange: otpFlow.handleMobileOtpChange,
        handleSendMobileOtp: otpFlow.handleSendMobileOtp,
        handleVerifyMobileOtp: otpFlow.handleVerifyMobileOtp,
        handleResendMobileOtp: otpFlow.handleResendMobileOtp,
        handleBackToMobileInput: otpFlow.handleBackToMobileInput,

        // Email handlers
        handleEmailChange: otpFlow.handleEmailChange,
        handleEmailOtpChange: otpFlow.handleEmailOtpChange,
        handleSendEmailOtp: otpFlow.handleSendEmailOtp,
        handleVerifyEmailOtp: otpFlow.handleVerifyEmailOtp,
        handleResendEmailOtp: otpFlow.handleResendEmailOtp,
        handleBackToEmailInput: otpFlow.handleBackToEmailInput,

        // Google Sign In handlers
        handleGoogleSignIn: otpFlow.handleGoogleSignIn,
        handleGoogleSignInError: otpFlow.handleGoogleSignInError,
        handleSendGoogleMobileOtp: otpFlow.handleSendGoogleMobileOtp,
        handleVerifyGoogleMobileOtp: otpFlow.handleVerifyGoogleMobileOtp,
        handleResendGoogleMobileOtp: otpFlow.handleResendGoogleMobileOtp
    }

    // Render appropriate component based on device
    return isMobile ? (
        <OtpVerificationModalMobile open={open} flowData={flowData} flowHandlers={flowHandlers} />
    ) : (
        <OtpVerificationModalDesktop
            open={open}
            flowData={flowData}
            flowHandlers={flowHandlers}
            onMenuClose={onMenuClose}
        />
    )
}

export { OtpVerificationModalWrapper }
export default OtpVerificationModalWrapper
export { OtpStep }
